import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  PieChart, 
  TrendingUp, 
  Download, 
  Filter, 
  Calendar,
  DollarSign,
  Package,
  Users,
  Star,
  Clock,
  Building,
  FileText,
  Eye,
  Printer
} from 'lucide-react';
import toast from 'react-hot-toast';

interface ReportData {
  volumePorMarca: {
    marca: string;
    quantidade: number;
    valor: number;
  }[];
  fornecedoresMaisRapidos: {
    fornecedor: string;
    tempoMedio: string;
    quantidade: number;
    avaliacao: number;
  }[];
  metricasSatisfacao: {
    mediaGeral: number;
    totalAvaliacoes: number;
    distribuicao: {
      excelente: number;
      muitoBom: number;
      bom: number;
      regular: number;
      ruim: number;
    };
  };
  volumePorPeriodo: {
    mes: string;
    pedidos: number;
    orcamentos: number;
    valor: number;
  }[];
  topFornecedores: {
    fornecedor: string;
    orcamentosEnviados: number;
    orcamentosAprovados: number;
    taxaAprovacao: number;
    avaliacaoMedia: number;
  }[];
}

interface AdvancedReportsProps {
  data: ReportData;
  onExport?: (type: string, data: any) => void;
  onFilter?: (filters: any) => void;
}

const AdvancedReports: React.FC<AdvancedReportsProps> = ({
  data,
  onExport,
  onFilter
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [showFilters, setShowFilters] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Visão Geral', icon: Eye },
    { id: 'volume', label: 'Volume por Marca', icon: BarChart3 },
    { id: 'performance', label: 'Performance', icon: TrendingUp },
    { id: 'satisfaction', label: 'Satisfação', icon: Star },
    { id: 'timeline', label: 'Evolução Temporal', icon: Calendar }
  ];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const handleExport = (type: string) => {
    if (onExport) {
      onExport(type, data);
      toast.success(`Relatório ${type} exportado com sucesso!`);
    } else {
      // Simular exportação
      const link = document.createElement('a');
      link.href = `data:text/csv;charset=utf-8,${encodeURIComponent('Dados do relatório')}`;
      link.download = `relatorio_${type}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Relatório ${type} exportado com sucesso!`);
    }
  };

  const renderVolumeChart = () => {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-dark">Volume por Marca</h3>
          <div className="flex space-x-2">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="form-select text-sm"
            >
              <option value="7d">Últimos 7 dias</option>
              <option value="30d">Últimos 30 dias</option>
              <option value="90d">Últimos 90 dias</option>
              <option value="1y">Último ano</option>
            </select>
            <button
              onClick={() => handleExport('volume')}
              className="btn btn-outline text-sm"
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.volumePorMarca.map((item, index) => (
            <div key={index} className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-dark">{item.marca}</h4>
                <Package className="h-5 w-5 text-red" />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-medium">Quantidade:</span>
                  <span className="font-semibold text-gray-dark">{item.quantidade}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-medium">Valor Total:</span>
                  <span className="font-semibold text-red">{formatCurrency(item.valor)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-red h-2 rounded-full"
                    style={{ 
                      width: `${(item.quantidade / Math.max(...data.volumePorMarca.map(i => i.quantidade))) * 100}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPerformanceChart = () => {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-dark">Fornecedores Mais Rápidos</h3>
          <button
            onClick={() => handleExport('performance')}
            className="btn btn-outline text-sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full table-auto">
            <thead>
              <tr className="border-b-2 border-gray-light">
                <th className="text-left py-3 px-4 font-semibold text-gray-dark">Fornecedor</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-dark">Tempo Médio</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-dark">Orçamentos</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-dark">Avaliação</th>
              </tr>
            </thead>
            <tbody>
              {data.fornecedoresMaisRapidos.map((item, index) => (
                <tr key={index} className="border-b border-gray-light hover:bg-gray-light">
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      <Building className="h-4 w-4 text-gray-medium" />
                      <span className="font-medium text-gray-dark">{item.fornecedor}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <Clock className="h-4 w-4 text-green-500" />
                      <span className="text-gray-dark">{item.tempoMedio}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="font-semibold text-gray-dark">{item.quantidade}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <span className="text-gray-dark">{item.avaliacao.toFixed(1)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSatisfactionChart = () => {
    const { mediaGeral, totalAvaliacoes, distribuicao } = data.metricasSatisfacao;
    
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-dark">Métricas de Satisfação</h3>
          <button
            onClick={() => handleExport('satisfaction')}
            className="btn btn-outline text-sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Média Geral */}
          <div className="card p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <Star className="h-8 w-8 text-yellow-400 fill-current" />
            </div>
            <div className="text-3xl font-bold text-gray-dark mb-2">
              {mediaGeral.toFixed(1)}
            </div>
            <div className="text-sm text-gray-medium">Média Geral</div>
          </div>

          {/* Total de Avaliações */}
          <div className="card p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <Users className="h-8 w-8 text-blue-500" />
            </div>
            <div className="text-3xl font-bold text-gray-dark mb-2">
              {totalAvaliacoes}
            </div>
            <div className="text-sm text-gray-medium">Total de Avaliações</div>
          </div>

          {/* Taxa de Satisfação */}
          <div className="card p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <TrendingUp className="h-8 w-8 text-green-500" />
            </div>
            <div className="text-3xl font-bold text-green-600 mb-2">
              {formatPercentage((distribuicao.excelente + distribuicao.muitoBom) / totalAvaliacoes)}
            </div>
            <div className="text-sm text-gray-medium">Taxa de Satisfação</div>
          </div>
        </div>

        {/* Distribuição de Avaliações */}
        <div className="card p-6">
          <h4 className="font-semibold text-gray-dark mb-4">Distribuição de Avaliações</h4>
          <div className="space-y-3">
            {[
              { label: 'Excelente', value: distribuicao.excelente, color: 'bg-green-500' },
              { label: 'Muito Bom', value: distribuicao.muitoBom, color: 'bg-blue-500' },
              { label: 'Bom', value: distribuicao.bom, color: 'bg-yellow-500' },
              { label: 'Regular', value: distribuicao.regular, color: 'bg-orange-500' },
              { label: 'Ruim', value: distribuicao.ruim, color: 'bg-red-500' }
            ].map((item, index) => (
              <div key={index} className="flex items-center space-x-3">
                <span className="text-sm text-gray-medium w-20">{item.label}</span>
                <div className="flex-1 bg-gray-200 rounded-full h-3">
                  <div
                    className={`${item.color} h-3 rounded-full`}
                    style={{ width: `${(item.value / totalAvaliacoes) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-gray-dark w-12">
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderTimelineChart = () => {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-dark">Evolução Temporal</h3>
          <button
            onClick={() => handleExport('timeline')}
            className="btn btn-outline text-sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.volumePorPeriodo.map((item, index) => (
            <div key={index} className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-dark">{item.mes}</h4>
                <Calendar className="h-5 w-5 text-red" />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-medium">Pedidos:</span>
                  <span className="font-semibold text-gray-dark">{item.pedidos}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-medium">Orçamentos:</span>
                  <span className="font-semibold text-gray-dark">{item.orcamentos}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-medium">Valor:</span>
                  <span className="font-semibold text-red">{formatCurrency(item.valor)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderOverview = () => {
    const totalPedidos = data.volumePorMarca.reduce((acc, item) => acc + item.quantidade, 0);
    const totalValor = data.volumePorMarca.reduce((acc, item) => acc + item.valor, 0);
    const mediaTempo = data.fornecedoresMaisRapidos.reduce((acc, item) => {
      const tempo = parseFloat(item.tempoMedio.replace('h', ''));
      return acc + tempo;
    }, 0) / data.fornecedoresMaisRapidos.length;

    return (
      <div className="space-y-6">
        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <Package className="h-8 w-8 text-red" />
            </div>
            <div className="text-3xl font-bold text-gray-dark mb-2">{totalPedidos}</div>
            <div className="text-sm text-gray-medium">Total de Pedidos</div>
          </div>

          <div className="card p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
            <div className="text-3xl font-bold text-gray-dark mb-2">{formatCurrency(totalValor)}</div>
            <div className="text-sm text-gray-medium">Valor Total</div>
          </div>

          <div className="card p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <Clock className="h-8 w-8 text-blue-500" />
            </div>
            <div className="text-3xl font-bold text-gray-dark mb-2">{mediaTempo.toFixed(1)}h</div>
            <div className="text-sm text-gray-medium">Tempo Médio</div>
          </div>

          <div className="card p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <Star className="h-8 w-8 text-yellow-400 fill-current" />
            </div>
            <div className="text-3xl font-bold text-gray-dark mb-2">
              {data.metricasSatisfacao.mediaGeral.toFixed(1)}
            </div>
            <div className="text-sm text-gray-medium">Avaliação Média</div>
          </div>
        </div>

        {/* Top Fornecedores */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-dark mb-4">Top Fornecedores</h3>
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr className="border-b-2 border-gray-light">
                  <th className="text-left py-3 px-4 font-semibold text-gray-dark">Fornecedor</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-dark">Enviados</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-dark">Aprovados</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-dark">Taxa</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-dark">Avaliação</th>
                </tr>
              </thead>
              <tbody>
                {data.topFornecedores.map((item, index) => (
                  <tr key={index} className="border-b border-gray-light hover:bg-gray-light">
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <Building className="h-4 w-4 text-gray-medium" />
                        <span className="font-medium text-gray-dark">{item.fornecedor}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="font-semibold text-gray-dark">{item.orcamentosEnviados}</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="font-semibold text-green-600">{item.orcamentosAprovados}</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="font-semibold text-blue-600">{formatPercentage(item.taxaAprovacao)}</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center space-x-1">
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                        <span className="text-gray-dark">{item.avaliacaoMedia.toFixed(1)}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-dark">Relatórios Avançados</h2>
          <p className="text-gray-medium">Análise detalhada de métricas e performance</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn btn-outline"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtros
          </button>
          <button
            onClick={() => handleExport('completo')}
            className="btn btn-primary"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar Completo
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-light">
        <div className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-red text-red'
                  : 'border-transparent text-gray-medium hover:text-gray-dark'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="card">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'volume' && renderVolumeChart()}
        {activeTab === 'performance' && renderPerformanceChart()}
        {activeTab === 'satisfaction' && renderSatisfactionChart()}
        {activeTab === 'timeline' && renderTimelineChart()}
      </div>
    </div>
  );
};

export default AdvancedReports; 